import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import logger from '../utils/logger';

// Import shared image cache
import { 
  loadedImageCache, 
  normalizeUrlForCache, 
  addToCache, 
  isInCache,
  isLoading,
  markAsLoading,
  markAsFinished,
  addLoadingCallback,
  getCachedBlobUrl,
  addToOriginalUrlCache,
  isInOriginalUrlCache,
  addBlobToCache,
  clearImageCache,
  getCacheSize,
  getCachedImages 
} from '../utils/imageCache';

logger.debug('OfflineImage', '🔄 v5.3 - two-tier cache system initialized');

// Re-export for backward compatibility
export { clearImageCache, getCacheSize, getCachedImages };

/**
 * OfflineImage component for online-only image loading with caching.
 * 
 * This component removes dependencies on offline storage and focuses on 
 * online-only image loading with loading states, error fallbacks, and
 * application-level caching to prevent redundant downloads.
 * 
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.itemId - Item ID for logging purposes
 * @param {string} props.alt - Alt text for the image
 * @param {string} props.fallbackSrc - Fallback image source
 * @param {Object} props.sx - MUI sx styling props
 * @param {boolean} props.prefetch - Whether to prefetch the image (legacy prop, now ignored)
 * @returns {JSX.Element} OfflineImage component
 */
const OfflineImage = ({ 
  src, 
  itemId, // kept for API compatibility but unused
  alt = '', 
  fallbackSrc = '/placeholder.png', 
  sx = {}, 
  imgProps = {},
  prefetch = true, // kept for API compatibility but unused
  ...rest
}) => {
  // Initialize state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  
  // Refs for tracking loaded sources and component ID
  const loadedSrcRef = useRef(null);
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  
  // DEBUG: Log component state changes
  logger.debug('OfflineImage', `Component ${itemId} state: loading=${loading}, error=${error}`);
  
  useEffect(() => {
    let mounted = true;
    
    const loadImage = async () => {
      if (!src) {
        logger.warn('OfflineImage', `No src provided for item ${itemId}`);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
        return;
      }
      
      // Skip loading if we've already loaded this exact src in this component instance
      if (loadedSrcRef.current === src) {
        logger.debug('OfflineImage', `Skipping reload of already loaded src for item ${itemId}`);
        return;
      }
      
      // Normalize URL for cache consistency (removes query parameters)
      const normalizedSrc = normalizeUrlForCache(src);
      
      logger.debug('OfflineImage', `Item ${itemId}: cacheSize=${loadedImageCache.size}, isInCache=${loadedImageCache.has(normalizedSrc)}`);
      
      // If another component is already loading this image, wait for it
      if (isLoading(src)) {
        logger.debug('OfflineImage', `⏳ ALREADY LOADING for item ${itemId} - SKIPPING DUPLICATE DOWNLOAD`);
        // Don't start another download, just wait and register callback
        if (mounted) {
          setLoading(true);
          setError(false);
          
          // Register callback to be notified when loading completes
          addLoadingCallback(src, (success, error) => {
            if (mounted) {
              if (success) {
                logger.debug('OfflineImage', `📞 CALLBACK SUCCESS for item ${itemId}`);
                const blobUrl = getCachedBlobUrl(src);
                setImageSrc(blobUrl || src);
                setLoading(false);
                setError(false);
                loadedSrcRef.current = src;
              } else {
                logger.debug('OfflineImage', `📞 CALLBACK ERROR for item ${itemId}`, error);
                setError(true);
                setLoading(false);
              }
            }
          });
        }
        return;
      }
      
      // Check if image is in original URL cache FIRST (for CORS-failed images)
      const isInOrigCache = isInOriginalUrlCache(src);
      logger.debug('OfflineImage', `ORIGINAL URL CACHE CHECK for item ${itemId}: isInCache=${isInOrigCache}`);
      if (isInOrigCache) {
        logger.debug('OfflineImage', `✅ ORIGINAL URL CACHE HIT for item ${itemId}`);
        if (mounted) {
          setImageSrc(src);
          setLoading(false);
          setError(false);
          loadedSrcRef.current = src;
        }
        return;
      }
      
      // Check if image is in blob cache (best case)
      if (isInCache(src)) {
        const cachedBlobUrl = getCachedBlobUrl(src);
        const isCurrentlyLoading = isLoading(src);
        logger.debug('OfflineImage', `✅ BLOB CACHE HIT for item ${itemId}`);
        if (mounted && cachedBlobUrl) {
          // Use cached blob URL to avoid network request
          setImageSrc(cachedBlobUrl);
          setLoading(false);
          setError(false);
          loadedSrcRef.current = src;
          return;
        }
        // If in cache but blob not available, check if image is currently being loaded
        if (isCurrentlyLoading) {
          // Another instance is still creating the blob, wait for callback
          logger.debug('OfflineImage', `⏳ WAITING for blob creation for item ${itemId}`);
          if (mounted) {
            setLoading(true);
            setError(false);
            addLoadingCallback(src, (success, error) => {
              if (!mounted) return;
              if (success) {
                const blobUrlAfter = getCachedBlobUrl(src);
                setImageSrc(blobUrlAfter || src);
                setLoading(false);
                setError(false);
                loadedSrcRef.current = src;
              } else {
                logger.debug('OfflineImage', `📞 CALLBACK (from cache wait) ERROR for item ${itemId}`, error);
                setError(true);
                setLoading(false);
              }
            });
          }
          return;
        }
        // If in blob cache but no blob URL and not loading, it means blob creation failed
        // Check original URL cache as fallback
        if (isInOriginalUrlCache(src)) {
          logger.debug('OfflineImage', `✅ BLOB CACHE MISS but ORIGINAL URL CACHE HIT for item ${itemId}`);
          if (mounted) {
            setImageSrc(src);
            setLoading(false);
            setError(false);
            loadedSrcRef.current = src;
          }
          return;
        }
      }
      
      logger.debug('OfflineImage', `❌ CACHE MISS for item ${itemId} - will download`);
      
      // Mark as loading to prevent other components from downloading the same image
      markAsLoading(src);
      
      try {
        setLoading(true);
        logger.debug('OfflineImage', `Loading image for item ${itemId}`);
        
        // Pre-load the image to ensure it's cached before setting src
        const img = new Image();
        
        // Set crossOrigin to allow canvas export for external images
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          logger.debug('OfflineImage', `🎯 img.onload fired for item ${itemId}`);
          if (mounted) {
            logger.debug('OfflineImage', `Setting image src for item ${itemId}`);
            setImageSrc(src);
            setLoading(false);
            setError(false);
            loadedSrcRef.current = src; // Mark this src as loaded in this component
            logger.debug('OfflineImage', `✅ State updated for item ${itemId}`);
          }
          // Try to create blob cache in background (non-blocking)
          addBlobToCache(src, img)
            .then(blobUrl => {
              if (mounted && blobUrl) {
                logger.debug('OfflineImage', `Background blob created for item ${itemId}`);
                // Add to blob cache only after successful blob creation
                addToCache(src);
                setImageSrc(blobUrl);
                markAsFinished(src, true);
              } else {
                logger.debug('OfflineImage', `Blob creation failed, adding to original URL cache for item ${itemId}`);
                // For images that can't be cached as blobs, add to original URL cache
                addToOriginalUrlCache(src);
                markAsFinished(src, true);
              }
            })
            .catch(error => {
              logger.warn('OfflineImage', `Background blob creation failed for item ${itemId}:`, error);
              // Even if blob creation failed, add to original URL cache to prevent future network requests
              addToOriginalUrlCache(src);
              markAsFinished(src, true, error);
            });
        };
        img.onerror = (event) => {
          markAsFinished(src, false, event); // Mark as finished loading with error
          if (mounted) {
            logger.error('OfflineImage', `Failed to load image for item ${itemId}`, event);
            setError(true);
            setLoading(false);
            // Don't add failed images to cache
          }
        };
        img.src = src;
      } catch (err) {
        markAsFinished(src, false, err); // Mark as finished loading with error
        logger.error('OfflineImage', `Error loading image for item ${itemId}:`, err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };
    
    loadImage();
    
    // Cleanup function
    return () => {
      mounted = false;
    };
  }, [src, itemId, prefetch]);
  
  // Handle image load error - use fallback directly
  const handleError = () => {
    logger.warn('OfflineImage', `Image failed to load for item ${itemId}, using fallback`);
    setError(true);
    setImageSrc(fallbackSrc);
  };
  
  if (loading) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        {...rest}
        sx={{ 
          minHeight: '100px',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          ...sx
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }
  
  if (error && !imageSrc) {
    return (
      <Box 
        component="img"
        src={fallbackSrc}
        alt={alt}
        crossOrigin="anonymous"
        {...imgProps}
        {...rest}
        sx={{ 
          objectFit: 'contain',
          ...sx
        }}
      />
    );
  }
  

  return (
    <Box 
      component="img"
      src={imageSrc}
      alt={alt}
      crossOrigin="anonymous"
      onError={handleError}
      {...imgProps}
      {...rest}
      sx={{ 
        objectFit: 'contain',
        ...sx 
      }}
    />
  );
};

export default OfflineImage;
